import { Box, Divider, Modal } from "@mui/material"
import {
  FocusContext,
  FocusHandler,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, {
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { useParams } from "react-router"
import { DGText } from "src/components/common/DGText"
import { Image } from "src/components/common/Image"
import empty from "src/components/images/empty.png"
import { comb, instantScroll, s, secondsToTime } from "src/utilities/common"
import { theme, TI } from "src/utilities/theme"

import { useAccessCheck } from "../../../../hooks/use-access-check"
import { useModalDismiss } from "../../../../hooks/use-modal-dismiss"
import { useSmoothNavigate } from "../../../../hooks/use-smooth-navigate"
import { useTransitions } from "../../../../hooks/use-transitions"
import useTranslate, { Namespaces } from "../../../../hooks/use-translate"
import {
  PlaybackContent,
  playerStore,
  TPlaylistItem,
  usePlayList,
} from "../../../../store/PlayerStore"
import { dimensions } from "../../../../utilities/constants"
import {
  AddFocusFunction,
  useFocusHelper,
} from "../../../../utilities/use-focus-helper"
import { LoginModal } from "../../../Settings/Login/LoginModal"
import { DetailFocusKeys } from "../../detail-focus-keys"
import { useDetailStore } from "../../DetailStore"
import { Buttons } from "../Buttons"
import { InfoModal } from "../InfoModal/InfoModal"

import { AlbumIcon } from "./AlbumIcon"
import { InfoIcon } from "./InfoIcon"
import { PlayIcon } from "./PlayIcon"
import { ThreeDots } from "./ThreeDots"

type ContextMenu = {
  open: boolean
  item: TPlaylistItem | undefined
  buttonMenuFocusKey: string | undefined
  returnFocus: string | undefined
  addFocus: AddFocusFunction
}
export const EditorialPlaylist = ({
  subscribeOpen,
  fk,
}: {
  subscribeOpen: comb<boolean>
  fk: MutableRefObject<string>
}) => {
  const page = useDetailStore((state) => state.page)
  const content = useDetailStore((state) => state.content)
  const t_common = useTranslate(Namespaces.Common)
  const playlist = usePlayList(content)
  const transition = useTransitions()
  const { type } = useParams()

  const setNavigationProps = playerStore((state) => state.setNavigationProps)

  const [loginOpen, setLoginOpen] = useState(false)
  const { accessCheck: accessCheckRaw } = useAccessCheck({
    loginOpen: comb.ine(loginOpen, setLoginOpen),
    subscribeOpen,
    focusKey: fk,
  })
  const setReturnFocus = playerStore((state) => state.setReturnFocus)
  const setContentPlayer = playerStore((state) => state.setContent)
  const infoOpen = useDetailStore((state) => state.infoOpen)
  const [albumId, setAlbumId] = useState<string | undefined>()

  const accessCheck = useCallback(() => accessCheckRaw({ page }), [page])

  const scrollRef = useRef<HTMLElement | null>(null)

  const { getFocus, addFocus } = useFocusHelper()
  useEffect(() => {
    getFocus()
  }, [])

  const [contextMenu, setContextMenu] = useState<ContextMenu>({
    open: false,
    item: undefined,
    returnFocus: undefined,
    buttonMenuFocusKey: undefined,
    addFocus,
  })

  if (!page || !content || content.__typename !== "EditorialPlaylist")
    return null
  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", p: s("80px") }}>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Image
            alt={""}
            src={page.albumCover}
            width={s("640px")}
            height={s("640px")}
            hasTransparentFallback
            sx={{
              borderRadius: s("16px"),
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${empty})`,
              backgroundPosition: "center",
              backgroundSize: s("640px"),
              backgroundColor: theme.palette.secondary.main,
            }}
          />
          <Box
            ref={scrollRef}
            sx={{
              transition,
              display: "flex",
              flexDirection: "column",
              ml: s("110px"),
              mt: s("64px"),
              alignItems: "flex-start",
            }}
          >
            <DGText
              tx={TI.new_largeTitle}
              sx={{ color: theme.palette.white.main }}
            >
              {page.title}
            </DGText>
            <DGText
              tx={TI.new_subheadline}
              sx={{ mt: s("16px"), color: theme.palette.new.surface[100] }}
            >
              {page.subtitle}
            </DGText>
            <Box
              sx={{
                mt: s("60px"),
                mb: s("60px"),
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <DGText
                tx={TI.new_footnote}
                sx={{ color: theme.palette.new.surface[100] }}
              >
                {t_common("playlist__items_count", [content.totalItems])}
              </DGText>
              <Box
                sx={{
                  height: s("6px"),
                  width: s("6px"),
                  backgroundColor: theme.palette.new.surface[100],
                  ml: s("16px"),
                  mr: s("16px"),
                  borderRadius: "50%",
                }}
              ></Box>
              <DGText
                tx={TI.new_footnote}
                sx={{ color: theme.palette.new.surface[100] }}
              >
                {t_common("video__duration_in_minutes", [
                  Math.round(content.totalDuration / 60),
                ])}
              </DGText>
            </Box>
            <Buttons accessCheck={accessCheck} scrollRef={scrollRef} />
            <Divider
              sx={{
                width: "100%",
                borderColor: theme.palette.white[20],
                mt: s("46px"),
                mb: s("60px"),
              }}
            />
            {playlist?.map((group) => {
              let lastWorkDisplayInfo: string | undefined = ""
              let displayWorkDisplayInfo = true
              return (
                <React.Fragment key={"EditorialPlaylistSection-" + group.id}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      width: "100%",
                      overflow: "hidden",
                      mb: s("40px"),
                    }}
                  >
                    <DGText
                      sx={{
                        width: s("481px"),
                        color: theme.palette.white.main,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 4,
                      }}
                      tx={TI.new_title3}
                    >
                      {group.title}
                    </DGText>
                    <DGText
                      sx={{
                        width: s("481px"),
                        color: theme.palette.new.surface[100],
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 7,
                      }}
                      tx={TI.new_caption2}
                    >
                      {group.description}
                    </DGText>
                  </Box>
                  {group.items?.map((item) => {
                    displayWorkDisplayInfo =
                      lastWorkDisplayInfo !== item.workDisplayInfo
                    lastWorkDisplayInfo = item.workDisplayInfo
                    return (
                      <PlaylistItem
                        key={"EditorialPlaylistSectionItem-" + item.id}
                        me={"Playlist-" + content.id}
                        contextMenu={comb.ine(contextMenu, setContextMenu)}
                        displayWorkDisplayInfo={displayWorkDisplayInfo}
                        item={item}
                        onFocus={({ top: y }, _, query) => {
                          if (
                            !(query && query.noScroll) &&
                            !!scrollRef?.current
                          ) {
                            instantScroll(scrollRef, query, transition)
                            scrollRef.current.style.transform =
                              "translate3d(0, " +
                              Math.min(0, -y + dimensions.height / 2 - s(200)) +
                              "px, 0)"
                          }
                        }}
                        fk={fk}
                        onPress={async () => {
                          if (!(await accessCheck())) return
                          setReturnFocus(fk.current)
                          setContentPlayer(
                            content as PlaybackContent,
                            item.id,
                            false,
                          )
                          setNavigationProps({
                            open: true,
                            id: page.id,
                            type,
                            isPreview: "false",
                            trackId: item.id,
                          })
                        }}
                      ></PlaylistItem>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </Box>
        </Box>
      </Box>
      <LoginModal
        isOpen={loginOpen}
        close={() => {
          setLoginOpen(false)
          setFocus(fk.current)
        }}
      />
      {contextMenu.open && (
        <ContextMenu
          accessCheck={accessCheck}
          albumId={comb.ine(albumId, setAlbumId)}
          contextMenu={comb.ine(contextMenu, setContextMenu)}
        ></ContextMenu>
      )}
      <InfoModal albumId={albumId} open={infoOpen} />
    </>
  )
}

export const ContextMenu = ({
  accessCheck,
  albumId,
  contextMenu,
}: {
  accessCheck: () => Promise<false | true>
  albumId: comb<string | undefined>
  contextMenu: comb<ContextMenu>
}) => {
  const t_common = useTranslate(Namespaces.Common)
  useModalDismiss(() => {
    if (contextMenu.val.buttonMenuFocusKey)
      setFocus(contextMenu.val.buttonMenuFocusKey)
    contextMenu.set({ ...contextMenu.val, open: false })
  })
  const navigate = useSmoothNavigate()
  const { id } = useParams()
  const setInfoOpen = useDetailStore((state) => state.setInfoOpen)
  const setReturnFocus = playerStore((state) => state.setReturnFocus)
  const setContentPlayer = playerStore((state) => state.setContent)
  const content = useDetailStore((state) => state.content)
  const setNavigationProps = playerStore((state) => state.setNavigationProps)
  const { type } = useParams()

  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
  })
  return (
    <Modal key={"playlistcontextmenu"} open={true}>
      <FocusContext.Provider value={focusKey}>
        <Box
          ref={ref}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: s("478px"),
              height: s("318px"),
              p: s("20px"),
              background: theme.palette.new.surface[0],
              boxShadow: s("0px 12px 36px rgba(0, 0, 0, 0.25)"),
              borderRadius: s("40px"),
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <ContextMenuChoiceButton
              ariaLabel={t_common("contextMenu__go_to_album")}
              onPress={() => {
                if (contextMenu.val.returnFocus)
                  contextMenu.val.addFocus({
                    forceKey: contextMenu.val.returnFocus,
                  })
                navigate(
                  `../detail/Album/${contextMenu.val.item?.album?.id}/0/${contextMenu.val.item?.index}/${id}`,
                )
              }}
              autoFocus
            >
              <AlbumIcon />
              <DGText sx={{ ml: s("20px") }} tx={TI.new_caption1}>
                {t_common("contextMenu__go_to_album")}
              </DGText>
            </ContextMenuChoiceButton>
            <ContextMenuChoiceButton
              ariaLabel={t_common("contextMenu__play_this_item")}
              onPress={async () => {
                if (!(await accessCheck())) return
                setReturnFocus(focusKey)
                setContentPlayer(
                  content as PlaybackContent,
                  contextMenu.val.item?.id,
                  false,
                )
                setNavigationProps({
                  open: true,
                  id: content?.id,
                  type,
                  isPreview: "false",
                  trackId: contextMenu.val.item?.id,
                })
              }}
            >
              <PlayIcon />
              <DGText sx={{ ml: s("20px") }} tx={TI.new_caption1}>
                {t_common("contextMenu__play_this_item")}
              </DGText>
            </ContextMenuChoiceButton>
            <ContextMenuChoiceButton
              ariaLabel={t_common("contextmenu__show_details")}
              focusKey={DetailFocusKeys.InfoButton}
              onPress={() => {
                albumId.set(contextMenu.val.item?.album?.id)
                setInfoOpen(true)
              }}
            >
              <InfoIcon />
              <DGText sx={{ ml: s("20px") }} tx={TI.new_caption1}>
                {t_common("contextmenu__show_details")}
              </DGText>
            </ContextMenuChoiceButton>
          </Box>
        </Box>
      </FocusContext.Provider>
    </Modal>
  )
}

const ContextMenuChoiceButton = ({
  children,
  autoFocus = false,
  onPress,
  focusKey,
  ariaLabel,
}: {
  children: ReactNode
  autoFocus?: boolean
  onPress: () => void
  focusKey?: string
  ariaLabel: string
}) => {
  const { ref, focused, focusSelf } = useFocusable({
    onEnterPress: onPress,
    focusKey,
  })

  useEffect(() => {
    if (autoFocus) focusSelf()
  }, [autoFocus])

  return (
    <Box
      ref={ref}
      onMouseEnter={() => focusSelf({ noScroll: true })}
      onMouseDown={onPress}
      tabIndex={0}
      aria-label={ariaLabel}
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        height: s("86px"),
        borderRadius: s("9999px"),
        backgroundColor: focused ? theme.palette.new.surface[100] : undefined,
      }}
    >
      {children}
    </Box>
  )
}

export const PlaylistItem = ({
  me,
  item,
  contextMenu,
  ...rest
}: {
  displayWorkDisplayInfo: boolean
  item: TPlaylistItem
  onFocus: FocusHandler
  onPress: () => void
  fk: MutableRefObject<string>
  contextMenu: comb<ContextMenu>
  me: string
}) => {
  const { ref, focusKey, hasFocusedChild } = useFocusable({
    trackChildren: true,
    preferredChildFocusKey: me + item.id,
    saveLastFocusedChild: false,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left", "right"],
  })

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{
          mb: s("40px"),
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "stretch",
          //transform: hasFocusedChild ? "scale(1.04)" : undefined,
        }}
      >
        <ContextMenuButton
          item={item}
          contextMenu={contextMenu}
          parentFocused={hasFocusedChild}
          me={me}
        ></ContextMenuButton>
        <PlaylistEntry
          me={me}
          item={item}
          parentFocused={hasFocusedChild}
          {...rest}
        />
      </Box>
    </FocusContext.Provider>
  )
}

const ContextMenuButton = ({
  me,
  item,
  parentFocused,
  contextMenu,
}: {
  me: string
  item: TPlaylistItem
  parentFocused: boolean
  contextMenu: comb<ContextMenu>
}) => {
  const t = useTranslate()
  const onEnterPress = useCallback(() => {
    contextMenu.set({
      ...contextMenu.val,
      open: true,
      item,
      buttonMenuFocusKey: focusKey,
      returnFocus: me + item.id,
    })
  }, [])
  const { ref, focused, focusKey, focusSelf } = useFocusable({
    onEnterPress,
  })
  return (
    <Box
      ref={ref}
      onMouseEnter={() => focusSelf({ noScroll: true })}
      onMouseDown={onEnterPress}
      tabIndex={0}
      aria-label={t("trial_cta_button__options")}
      sx={{
        width: s("64px"),
        height: s("64px"),
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: focused ? theme.palette.black.buttonBlue : "transparent",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: parentFocused ? 1 : 0,
        backgroundColor: focused ? theme.palette.secondary.variant : undefined,
      }}
    >
      <ThreeDots></ThreeDots>
    </Box>
  )
}

const PlaylistEntry = ({
  displayWorkDisplayInfo,
  item,
  onFocus,
  onPress,
  fk,
  me,
}: {
  displayWorkDisplayInfo: boolean
  item: TPlaylistItem
  onFocus: FocusHandler
  onPress: () => void
  parentFocused: boolean
  fk: MutableRefObject<string>
  me: string
}) => {
  const onEnterPress = useCallback(() => {
    onPress()
    fk.current = focusKey
  }, [])
  const { ref, focused, focusKey, focusSelf } = useFocusable({
    onFocus,
    focusKey: me + item.id,
    onEnterPress,
  })
  return (
    <Box
      ref={ref}
      onMouseEnter={() => focusSelf({ noScroll: true })}
      onMouseDown={onEnterPress}
      tabIndex={0}
      aria-label={
        (displayWorkDisplayInfo ? item.workDisplayInfo + ". " : "") +
        item.title +
        ". " +
        item.subtitle
      }
      sx={{
        ml: s("20px"),
        color: theme.palette.white.main,
        borderRadius: s("20px"),
        width: s("926px"),
        backgroundColor: focused ? theme.palette.secondary.variant : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          width: s("832px"),
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            ml: s("40px"),
            mt: s("40px"),
          }}
        >
          {displayWorkDisplayInfo && (
            <DGText
              tx={TI.new_caption1}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
              }}
            >
              {item.workDisplayInfo}
            </DGText>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                mt: s("20px"),
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: s("734px"),
              }}
            >
              <Image
                style={{ width: s("180px"), height: s("180px") }}
                src={item.album?.picture}
              ></Image>
              <DGText
                sx={{
                  ml: s("40px"),
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 3,
                }}
                tx={TI.new_caption1}
              >
                {item.title}
              </DGText>
            </Box>
          </Box>
          <Box
            component={"span"}
            sx={{
              mt: s("20px"),
              mb: s("40px"),
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              "& > :not(:last-child)": {
                mr: s("10px"),
              },
            }}
          >
            {item.subtitleWhite && (
              <DGText
                sx={{
                  display: "inline-flex",
                  color: theme.palette.white.main,
                }}
                tx={TI.new_caption2}
              >
                {item.subtitleWhite}
              </DGText>
            )}
            {item.subtitleGray && (
              <DGText
                sx={{
                  display: "inline-flex",
                  color: theme.palette.new.surface["200"],
                }}
                tx={TI.new_caption2}
              >
                {item.subtitleGray}
              </DGText>
            )}
          </Box>
        </Box>
        <DGText
          sx={{
            alignSelf: "center",
            ml: s("40px"),
            color: theme.palette.new.surface[100],
          }}
          tx={TI.new_caption2}
        >
          {secondsToTime(item.duration)}
        </DGText>
      </Box>
    </Box>
  )
}
